// utils/validations.ts - Funciones de utilidad y validación

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
  }).format(amount);
};

export const formatNumber = (number: number): string => {
  return new Intl.NumberFormat('es-CL').format(number);
};

export const validateEmail = (email: string): string => {
  if (!email) return 'El email es obligatorio';
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) return 'Ingresa un email válido';
  return '';
};

export const validateTelefonoChileno = (telefono: string, required = true): string => {
  // Limpiar espacios y obtener solo números
  const cleanPhone = telefono.replace(/\s/g, '').replace(/^\+/, '');
  
  // Si solo tiene el prefijo +56 o está vacío, considerarlo como vacío
  if (!cleanPhone || cleanPhone === '56') {
    if (required) return 'El teléfono es obligatorio';
    return '';
  }
  
  // Formatos válidos después de limpiar: 56912345678, 912345678, 5621234567
  const phoneRegex = /^(56)?([98][0-9]{8}|[2][0-9]{7,8})$/;
  if (!phoneRegex.test(cleanPhone)) {
    return 'Ingresa un número de teléfono chileno válido (ej: 9 1234 5678)';
  }
  
  return '';
};

export const formatTelefonoChileno = (telefono: string): string => {
  const clean = telefono.replace(/\D/g, '');
  
  if (!clean) return telefono;
  
  // Si ya tiene código de país
  if (clean.startsWith('56') && clean.length >= 11) {
    const formatted = clean.slice(0, 11);
    return `+${formatted.slice(0, 2)} ${formatted.slice(2, 3)} ${formatted.slice(3, 7)} ${formatted.slice(7)}`;
  }
  
  // Si es número móvil (empieza con 9)
  if (clean.startsWith('9') && clean.length === 9) {
    return `+56 ${clean.slice(0, 1)} ${clean.slice(1, 5)} ${clean.slice(5)}`;
  }
  
  // Si es número fijo (8 dígitos)
  if (clean.length === 8) {
    return `+56 2 ${clean.slice(0, 4)} ${clean.slice(4)}`;
  }
  
  return telefono;
};

export const validateRUT = (rut: string): boolean => {
  // Si está vacío, es válido (el campo no es obligatorio en algunas situaciones)
  if (!rut || rut.trim() === '') {
    return true;
  }
  
  // Limpiar: quitar puntos, guiones y espacios, dejar solo números y K
  const cleanRUT = rut.replace(/[^0-9kK]/g, '');
  
  // RUT chileno tiene entre 7 y 9 caracteres (sin puntos ni guión)
  // 7 dígitos: RUTs antiguos (ej: 1.234.567-8)
  // 8 dígitos: RUTs comunes (ej: 12.345.678-9)
  // 9 dígitos: RUTs modernos (ej: 123.456.789-0)
  if (cleanRUT.length < 7 || cleanRUT.length > 9) {
    return false;
  }
  
  const body = cleanRUT.slice(0, -1);
  const dv = cleanRUT.slice(-1).toUpperCase();
  
  // Calcular dígito verificador usando Módulo 11
  let sum = 0;
  let multiplier = 2;
  
  for (let i = body.length - 1; i >= 0; i--) {
    sum += parseInt(body[i]) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }
  
  const remainder = sum % 11;
  const calculatedDV = remainder === 0 ? '0' : remainder === 1 ? 'K' : String(11 - remainder);
  
  return dv === calculatedDV;
};

export const formatRUT = (rut: string): string => {
  // Limpiar todo excepto números y K
  const clean = rut.replace(/[^0-9kK]/g, '').toUpperCase();
  
  if (clean.length === 0) {
    return '';
  }
  
  // Si tiene menos de 2 caracteres, no formatear aún
  if (clean.length < 2) {
    return clean;
  }
  
  // Para RUTs completos (7-9 dígitos), separar cuerpo y DV
  if (clean.length >= 7) {
    const body = clean.slice(0, -1);
    const dv = clean.slice(-1);
    const formattedBody = body.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${formattedBody}-${dv}`;
  }
  
  // Para RUTs incompletos, solo agregar puntos sin guion
  return clean.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

export const validateRequired = (value: string): boolean => {
  return value.trim().length > 0;
};

export const validateMinLength = (value: string, minLength: number): boolean => {
  return value.length >= minLength;
};

export const validateMaxLength = (value: string, maxLength: number): boolean => {
  return value.length <= maxLength;
};

export const validateNumeric = (value: string): boolean => {
  return /^\d+$/.test(value);
};

export const validatePositiveNumber = (value: number): boolean => {
  return value > 0;
};

export const validateAlphanumeric = (value: string): boolean => {
  return /^[a-zA-Z0-9\sáéíóúñüÁÉÍÓÚÑÜ]+$/.test(value);
};

export const validateNoSpecialChars = (value: string): boolean => {
  return /^[a-zA-Z0-9\s.,áéíóúñüÁÉÍÓÚÑÜ-]+$/.test(value);
};

export const sanitizeInput = (input: string): string => {
  return input.trim().replace(/[<>]/g, '');
};

export const capitalizeFirst = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const capitalizeWords = (str: string): string => {
  return str.replace(/\w\S*/g, (txt) => 
    txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  );
};

// Agregar funciones de validación que faltan para compatibilidad
export const validateUsername = (username: string): string => {
  if (!username) return 'El nombre de usuario es obligatorio';
  if (username.length < 3) return 'El nombre de usuario debe tener al menos 3 caracteres';
  if (username.length > 30) return 'El nombre de usuario no puede tener más de 30 caracteres';
  if (!/^[a-zA-Z0-9_]+$/.test(username)) return 'Solo se permiten letras, números y guiones bajos';
  return '';
};

export const validateName = (name: string, required = true): string => {
  if (required && !name) return 'El nombre es obligatorio';
  if (name && name.length < 2) return 'El nombre debe tener al menos 2 caracteres';
  if (name && !/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(name)) return 'Solo se permiten letras y espacios';
  return '';
};

export const validatePassword = (password: string): string => {
  if (!password) return 'La contraseña es obligatoria';
  if (password.length < 8) return 'La contraseña debe tener al menos 8 caracteres';
  if (!/(?=.*[a-z])/.test(password)) return 'La contraseña debe tener al menos una letra minúscula';
  if (!/(?=.*[A-Z])/.test(password)) return 'La contraseña debe tener al menos una letra mayúscula';
  if (!/(?=.*\d)/.test(password)) return 'La contraseña debe tener al menos un número';
  return '';
};

export const validatePasswordConfirmation = (password: string, confirmPassword: string): string => {
  if (!confirmPassword) return 'Confirma tu contraseña';
  if (confirmPassword !== password) return 'Las contraseñas no coinciden';
  return '';
};

